import React, { useState, useEffect, useCallback } from 'react';

const API_BASE = `${process.env.REACT_APP_API_URL}/api`;

const initialFormState = {
    code: '',
    discount_type: 'percent',
    discount_value: '',
    rule_type: 'manual',
    applies_to: 'both',
    duration: 'once',
    max_redemptions: '',
    per_user_limit: '1',
    expires_at: '',
    active: true,
};

const CouponManagerPage = () => {
    const [coupons, setCoupons] = useState([]);
    const [formData, setFormData] = useState(initialFormState);
    const [statusMsg, setStatusMsg] = useState(null); // { type, text }
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);

    const fetchCoupons = useCallback(async () => {
        setLoadError('');
        try {
            const res = await fetch(`${API_BASE}/admin_list_coupons.php`, { credentials: 'include' });
            const data = await res.json();
            if (data.status === 'success') setCoupons(data.coupons);
            else setLoadError(data.message || 'Failed to load coupons.');
        } catch (err) {
            setLoadError('Network error loading coupons.');
        }
    }, []);

    useEffect(() => { fetchCoupons(); }, [fetchCoupons]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setStatusMsg(null);
        const payload = {
            ...formData,
            discount_value: formData.discount_value === '' ? null : parseFloat(formData.discount_value),
            max_redemptions: formData.max_redemptions === '' ? null : parseInt(formData.max_redemptions, 10),
            per_user_limit: formData.per_user_limit === '' ? 1 : parseInt(formData.per_user_limit, 10),
            expires_at: formData.expires_at || null,
            active: formData.active ? 1 : 0,
        };
        try {
            const res = await fetch(`${API_BASE}/admin_create_coupon.php`, {
                method: 'POST', credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const result = await res.json();
            if (result.status === 'success') {
                setStatusMsg({ type: 'success', text: `Coupon “${formData.code}” created.` });
                setFormData(initialFormState);
                fetchCoupons();
            } else {
                setStatusMsg({ type: 'error', text: result.message || 'Could not create coupon.' });
            }
        } catch (err) {
            setStatusMsg({ type: 'error', text: 'Network error: ' + err.message });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAction = async (id, action) => {
        try {
            const res = await fetch(`${API_BASE}/admin_update_coupon.php`, {
                method: 'POST', credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id, action }),
            });
            const result = await res.json();
            if (result.status === 'success') {
                setConfirmDeleteId(null);
                fetchCoupons();
            } else {
                setStatusMsg({ type: 'error', text: result.message || 'Action failed.' });
            }
        } catch (err) {
            setStatusMsg({ type: 'error', text: 'Network error: ' + err.message });
        }
    };

    const formatDiscount = (c) =>
        c.discount_type === 'percent' ? `${parseFloat(c.discount_value)}%` : `$${parseFloat(c.discount_value).toFixed(2)}`;

    const formatDate = (dt) =>
        !dt ? '—' : new Date(dt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

    const labelClass = 'block text-[11px] font-semibold uppercase tracking-wider text-neutral-400 mb-1.5';
    const inputClass = 'w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-neutral-600 focus:outline-none focus:ring-2 focus:ring-cyan-500/60 focus:border-transparent';
    const cardClass = 'rounded-2xl border border-white/10 bg-[#0b0e14]/60 backdrop-blur-sm';

    return (
        // Spacing is owned by AdminLayout's pane; fixed width avoids
        // re-centering shifts when switching admin tabs.
        <div className="text-white">
            <div className="max-w-6xl">
                <header className="mb-8">
                    <h1 className="text-3xl font-bold tracking-tight">Coupons</h1>
                    <p className="text-sm text-neutral-400 mt-1">Create and manage discount codes for checkout.</p>
                </header>

                {statusMsg && (
                    <div className={`mb-6 px-4 py-3 rounded-lg text-sm font-medium border ${statusMsg.type === 'error'
                        ? 'bg-red-500/10 border-red-500/30 text-red-300'
                        : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'}`}>
                        {statusMsg.text}
                    </div>
                )}

                {/* Create form */}
                <section className={`${cardClass} p-6 sm:p-7 mb-10`}>
                    <div className="flex items-center gap-2 mb-5">
                        <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" />
                        <h2 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-400">Create New Coupon</h2>
                    </div>
                    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div className="md:col-span-2">
                            <label className={labelClass}>Coupon Code *</label>
                            <input required type="text" name="code" value={formData.code} onChange={handleChange}
                                placeholder="e.g. SAVE20" autoComplete="off" className={`${inputClass} font-mono tracking-wide`} />
                        </div>

                        <div>
                            <label className={labelClass}>Discount Type *</label>
                            <select name="discount_type" value={formData.discount_type} onChange={handleChange} className={inputClass}>
                                <option value="percent">Percent (%)</option>
                                <option value="amount">Amount ($)</option>
                            </select>
                        </div>

                        <div>
                            <label className={labelClass}>Discount Value * {formData.discount_type === 'percent' ? '(1–100)' : '(USD)'}</label>
                            <input required type="number" name="discount_value" value={formData.discount_value} onChange={handleChange}
                                min="0.01" max={formData.discount_type === 'percent' ? '100' : undefined} step="0.01" className={inputClass} />
                        </div>

                        <div>
                            <label className={labelClass}>Rule Type</label>
                            <select name="rule_type" value={formData.rule_type} onChange={handleChange} className={inputClass}>
                                <option value="manual">Manual (anyone)</option>
                                <option value="new_user">New User</option>
                                <option value="birthday">Birthday</option>
                            </select>
                        </div>

                        <div>
                            <label className={labelClass}>Applies To</label>
                            <select name="applies_to" value={formData.applies_to} onChange={handleChange} className={inputClass}>
                                <option value="both">Both plans</option>
                                <option value="monthly">Monthly</option>
                                <option value="annual">Annual</option>
                            </select>
                        </div>

                        <div>
                            <label className={labelClass}>Duration</label>
                            <select name="duration" value={formData.duration} onChange={handleChange} className={inputClass}>
                                <option value="once">Once (first payment)</option>
                                <option value="forever">Forever (every renewal)</option>
                            </select>
                        </div>

                        <div>
                            <label className={labelClass}>Max Redemptions</label>
                            <input type="number" name="max_redemptions" value={formData.max_redemptions} onChange={handleChange}
                                min="1" step="1" placeholder="Unlimited" className={inputClass} />
                        </div>

                        <div>
                            <label className={labelClass}>Per User Limit</label>
                            <input type="number" name="per_user_limit" value={formData.per_user_limit} onChange={handleChange}
                                min="1" step="1" className={inputClass} />
                        </div>

                        <div>
                            <label className={labelClass}>Expires At</label>
                            <input type="datetime-local" name="expires_at" value={formData.expires_at} onChange={handleChange} className={`${inputClass} [color-scheme:dark]`} />
                        </div>

                        <label htmlFor="active" className="flex items-center gap-3 cursor-pointer select-none md:pt-7">
                            <input type="checkbox" id="active" name="active" checked={formData.active} onChange={handleChange}
                                className="w-5 h-5 rounded accent-cyan-500" />
                            <span className="text-sm font-medium text-neutral-200">Active on creation</span>
                        </label>

                        <button type="submit" disabled={isSubmitting}
                            className="md:col-span-2 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold py-3 rounded-lg disabled:opacity-50 transition-colors shadow-lg shadow-cyan-500/20">
                            {isSubmitting ? 'Creating…' : 'Create Coupon'}
                        </button>
                    </form>
                </section>

                {/* List */}
                <div className="flex items-center gap-2 mb-4">
                    <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" />
                    <h2 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-400">Existing Coupons</h2>
                    <span className="text-[11px] text-neutral-600">{coupons.length}</span>
                </div>

                {loadError && (
                    <div className="mb-4 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm">{loadError}</div>
                )}

                {coupons.length === 0 && !loadError ? (
                    <div className={`${cardClass} p-10 text-center text-neutral-500 text-sm`}>No coupons yet — create one above.</div>
                ) : (
                    <div className={`${cardClass} overflow-x-auto`}>
                        <table className="w-full text-sm text-left">
                            <thead className="text-[11px] uppercase tracking-wider text-neutral-500 border-b border-white/10">
                                <tr>
                                    <th className="px-4 py-3 font-semibold">Code</th>
                                    <th className="px-4 py-3 font-semibold">Discount</th>
                                    <th className="px-4 py-3 font-semibold">Rule</th>
                                    <th className="px-4 py-3 font-semibold">Applies</th>
                                    <th className="px-4 py-3 font-semibold">Duration</th>
                                    <th className="px-4 py-3 font-semibold">Redeemed</th>
                                    <th className="px-4 py-3 font-semibold">Expires</th>
                                    <th className="px-4 py-3 font-semibold">Status</th>
                                    <th className="px-4 py-3 font-semibold text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                                {coupons.map((c) => {
                                    const active = parseInt(c.active);
                                    return (
                                        <tr key={c.id} className="hover:bg-white/[0.03] transition-colors">
                                            <td className="px-4 py-3 font-mono font-semibold text-white">{c.code}</td>
                                            <td className="px-4 py-3 text-cyan-300 font-semibold">{formatDiscount(c)}</td>
                                            <td className="px-4 py-3 capitalize text-neutral-400">{c.rule_type.replace('_', ' ')}</td>
                                            <td className="px-4 py-3 capitalize text-neutral-400">{c.applies_to}</td>
                                            <td className="px-4 py-3 capitalize text-neutral-400">{c.duration}</td>
                                            <td className="px-4 py-3 text-neutral-400">{c.redeemed_count}{c.max_redemptions ? ` / ${c.max_redemptions}` : ' / ∞'}</td>
                                            <td className="px-4 py-3 text-neutral-400">{formatDate(c.expires_at)}</td>
                                            <td className="px-4 py-3">
                                                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${active
                                                    ? 'bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/30'
                                                    : 'bg-white/5 text-neutral-500 ring-1 ring-white/10'}`}>
                                                    <span className={`h-1.5 w-1.5 rounded-full ${active ? 'bg-emerald-400' : 'bg-neutral-500'}`} />
                                                    {active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex gap-1.5 justify-end">
                                                    <button onClick={() => handleAction(c.id, 'toggle')}
                                                        className="px-3 py-1.5 text-xs font-medium rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-neutral-200 transition-colors">
                                                        {active ? 'Deactivate' : 'Activate'}
                                                    </button>
                                                    {confirmDeleteId === c.id ? (
                                                        <>
                                                            <button onClick={() => handleAction(c.id, 'delete')}
                                                                className="px-3 py-1.5 text-xs font-semibold rounded-md bg-red-600 hover:bg-red-500 text-white transition-colors">
                                                                Confirm
                                                            </button>
                                                            <button onClick={() => setConfirmDeleteId(null)}
                                                                className="px-2.5 py-1.5 text-xs rounded-md text-neutral-400 hover:text-white transition-colors">
                                                                Cancel
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <button onClick={() => setConfirmDeleteId(c.id)}
                                                            className="px-3 py-1.5 text-xs font-medium rounded-md bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-300 transition-colors">
                                                            Delete
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CouponManagerPage;
